import {
  IAppliedOffer,
  IIncentiveSelection,
} from '@fhs-legacy/frontend/src/generated/graphql-gateway';
import { actions } from '@fhs-legacy/frontend/src/state/global-state';
import { AppDispatch } from '@fhs-legacy/frontend/src/state/global-state/store';
import {
  IAppliedRewards,
  IUseGetAvailableRewards,
} from '@fhs-legacy/frontend/src/state/loyalty/hooks/types';
import { IMenuOptionsContext } from '@fhs-legacy/frontend/src/state/menu-options';
import { IPriceItemOptionModifierInput } from '@fhs-legacy/frontend/src/state/menu-options/hooks/use-pricing-function';
import { OffersValue } from '@fhs-legacy/frontend/src/state/offers';
import { CartEntryType } from '@fhs-legacy/frontend/src/utils/cart';
import { computeCartTotal } from '@fhs-legacy/frontend/src/utils/cart/helper';
import { ICartEntry, IOfferDiscount } from '@rbi-ctg/menu';

export function shouldOfferV2BeRemoved(
  item: ICartEntry,
  selectedOfferSelections: IIncentiveSelection[]
) {
  // also required in getOfferText/removeItemMessage
  const isASelectionFromOffer = selectedOfferSelections?.find(
    selection => selection.lineId === item.cartId
  );
  return isASelectionFromOffer;
}

export function shouldOfferBeRemoved(
  currentEntries: ICartEntry[],
  item: ICartEntry,
  offers: OffersValue,
  loyaltyEnabled: boolean,
  appliedLoyaltyRewards: IAppliedRewards,
  discountAppliedCmsOffers: any
) {
  const cartEntriesWithRemovedItem = currentEntries.filter(
    (entry: ICartEntry) => entry._id !== item._id
  );
  const offerDetails = {
    selectedOffer: offers.selectedOffer,
    selectedOfferCartEntry: offers.selectedOfferCartEntry,
    selectedOfferPrice: offers.selectedOfferPrice,
  };

  let total = computeCartTotal(offerDetails, cartEntriesWithRemovedItem, {
    loyaltyEnabled,
    appliedLoyaltyRewards,
    appliedLoyaltyOfferDiscount: discountAppliedCmsOffers?.incentives?.[0] as IOfferDiscount,
  });
  total = total < 0 ? 0 : total;

  const offerPrice = (offers?.selectedOffer?.option as IOfferDiscount)?.discountValue || 0;
  // TODO: this line does not account for percentage type discounts
  return total < offerPrice * 100;
}

export function removeOfferFromCart(offers: OffersValue) {
  const { selectedOffer: selectedCBAOffer, clearSelectedOffer } = offers;

  return selectedCBAOffer && clearSelectedOffer();
}

export function removeRewardIfNeeded(
  cartEntry: ICartEntry,
  dispatch: AppDispatch,
  getAvailableRewardFromCartEntry: IUseGetAvailableRewards['getAvailableRewardFromCartEntry'],
  appliedLoyaltyRewards: IAppliedRewards,
  loyaltyEnabled: boolean
) {
  const { cartId } = cartEntry;
  const cartEntryReward = getAvailableRewardFromCartEntry(cartEntry);

  const isRewardApplied = !!appliedLoyaltyRewards?.[cartId]?.timesApplied;
  if (loyaltyEnabled && isRewardApplied && cartEntryReward) {
    dispatch(
      actions.loyalty.removeAppliedReward({
        rewardBenefitId: cartEntryReward.rewardBenefitId,
        cartId,
      })
    );
  }
}

export function repriceCartEntriesHelper(
  entries: ICartEntry[],
  pricingFunction: IMenuOptionsContext['pricingFunction'],
  priceForItemOptionModifier: (obj: IPriceItemOptionModifierInput) => number,
  priceForItemInComboSlotSelection: IMenuOptionsContext['priceForItemInComboSlotSelection'],
  appliedOffers: IAppliedOffer[],
  parentEntry?: any,
  mainCombo?: any
  // TODO: type
) {
  return entries.map(entry => {
    const hasPrice = entry.price !== undefined;
    const isItem = entry.type === CartEntryType.item;
    const isCombo = entry.type === CartEntryType.combo;
    const isSwap = appliedOffers.some(offer =>
      entry?.children.length ? offer.cartId === entry.cartId : offer?.swap?.cartId === entry.cartId
    );
    const isComboSlot = parentEntry?.type === CartEntryType.comboSlot;

    if (isItem) {
      // reprices an item and its grandchild itemOptionModifiers in one go.
      const priceFunc = isComboSlot
        ? priceForItemInComboSlotSelection({
            combo: mainCombo,
            comboSlot: parentEntry,
            selectedItem: entry as any,
          })
        : pricingFunction(entry, entry.quantity);
      return {
        ...entry,
        // prices are not defined on main items in combos, we need to preserve this
        ...(!isSwap && hasPrice && { price: priceFunc }),
        children: (entry.children || []).map((itemOption: any) => ({
          ...itemOption,
          children: (itemOption.children || []).map(modifier => ({
            ...modifier,
            price: priceForItemOptionModifier({ item: entry, itemOption, modifier }),
          })),
        })),
      };
    }

    return {
      ...entry,
      ...(!isSwap && isCombo && hasPrice && { price: pricingFunction(entry, entry.quantity) }),
      children: repriceCartEntriesHelper(
        entry.children,
        pricingFunction,
        priceForItemOptionModifier,
        priceForItemInComboSlotSelection,
        appliedOffers,
        entry,
        isCombo ? entry : mainCombo
      ),
    };
  });
}
